import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DRMT_EXCEL_LABEL } from "../constData/excelColumn";
import { getChangeLogExcelTableData, resetChangeLogExcelTableData } from "../redux/reducers/changeLogExcelDataSlice";
import { exportToExcel } from "../helpers";

const useExportChangeLogExcelData = (process) => {
  const [snackbarState, setSnackbarState] = useState({
    export_in_progress: false,
    download_started: false,
    download_completed: false,
    no_rec_to_download: false,
  });

  const [excelDataArray, setExcelDataArray] = useState([]);
  const dispatch = useDispatch();

  const { drmExcelTableData, isLoading: isLoadingExcel } = useSelector(
    (state) => state?.changeLogExcelData
  );

  const { drmChangeLogTableData } = useSelector((state) => state.drmChangeLog);
  const { tableFilter } = useSelector((state) => state.drmFilterPayload);
  const { dailyTableFilter } = useSelector((state) => state.drmDailyFilterPayload);

  const isBid = process === "1";

  const allowedKeys = useMemo(() => DRMT_EXCEL_LABEL.map((n) => ({ key: n.key, name: n.name })), []);

  const requestPayload = useMemo(() => {
    if (isBid) {
      return { process, filters: tableFilter?.filters };
    }
    return { process, filters: dailyTableFilter?.filters };
  }, [tableFilter, dailyTableFilter, process, isBid]);

  const setSnackbar = (key, value) => {
    setSnackbarState((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpen = (key) => snackbarState[key];
  const handleClose = (key) => setSnackbar(key, false);

  const excelDataFormatter = useCallback((excelTableData) => {
    if (!excelTableData?.drmMaintenanceDetails) return;

    const formatted = excelTableData.drmMaintenanceDetails.map((item) =>
      Object.fromEntries(
        allowedKeys.map(({ key, name }) => [name, item[key] ?? ""])
      )
    );

    setExcelDataArray(formatted);
  }, [allowedKeys]);

  const handleDownloadRecodes = useCallback(() => {
    const totalRecords = drmChangeLogTableData?.totalRecords || 0;

    if (isLoadingExcel) {
      setSnackbar("export_in_progress", true);
    } else if (Number(totalRecords) > 0) {
      setSnackbar("download_started", true);
      dispatch(getChangeLogExcelTableData({
        reqBody: requestPayload,
        totalPages: Math.ceil(totalRecords / 1000),
        tableName: "drmChangeLogTableData",
      }));
    } else {
      setSnackbar("no_rec_to_download", true);
    }
  }, [drmChangeLogTableData?.totalRecords, isLoadingExcel, dispatch, requestPayload]);

  const triggerExcelDownload = useCallback(() => {
    const fileName = isBid ? "BidChangeLogExcelExport" : "DailyChangeLogExcelExport";
    if (excelDataArray.length > 0) {
      exportToExcel(excelDataArray, fileName);
      setSnackbar("download_completed", true);
      dispatch(resetChangeLogExcelTableData());
      setExcelDataArray([]);
    }
  }, [excelDataArray, isBid, dispatch]);

  useEffect(() => {
    if (drmExcelTableData?.drmMaintenanceDetails?.length > 0) {
      excelDataFormatter(drmExcelTableData);
    }
  }, [drmExcelTableData, excelDataFormatter]);

  useEffect(() => {
    if (excelDataArray.length > 0) {
      const timer = setTimeout(() => {
        triggerExcelDownload();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [excelDataArray, triggerExcelDownload]);

  return {
    handleDownloadRecodes,
    handleOpen,
    handleClose,
    snackbarState,
  };
};

export default useExportChangeLogExcelData;
